import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Users,
    Shield,
    Settings,
    BarChart3,
    Bell,
    Search,
    TrendingUp,
    TrendingDown,
    MoreVertical,
    Calendar,
    FileText,
    Database,
    Activity,
    LogOut,
    Menu,
    X
} from "lucide-react";
import { getCurrentUser, logoutUser } from "../../utils/auth";
import dashboardImg from "../../imgs/dashboard.svg";

const AdminDashboard = () => {
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const user = getCurrentUser();
    const navigate = useNavigate();

    const handleLogout = () => {
        logoutUser();
        navigate("/login");
    };

    const menuItems = [
        { path: "/admin/manage-roles", label: "Manage Roles", icon: <Shield className="w-5 h-5" /> },
        { path: "/admin/manage-permissions", label: "Permissions", icon: <Users className="w-5 h-5" /> },
        { path: "/admin/view-logs", label: "Activity Logs", icon: <Activity className="w-5 h-5" /> },
        { path: "/admin/settings", label: "Settings", icon: <Settings className="w-5 h-5" /> },
    ];

    const stats = [
        {
            label: "Total Users",
            value: "1,428",
            change: "+12.5%",
            trend: "up",
            icon: <Users className="w-6 h-6 text-primary-600" />,
            color: "from-primary-500 to-primary-600"
        },
        {
            label: "Active Projects",
            value: "48",
            change: "+3.2%",
            trend: "up",
            icon: <FileText className="w-6 h-6 text-secondary-600" />,
            color: "from-secondary-500 to-secondary-600"
        },
        {
            label: "System Health",
            value: "99.8%",
            change: "+0.2%",
            trend: "up",
            icon: <Database className="w-6 h-6 text-green-600" />,
            color: "from-green-500 to-green-600"
        },
        {
            label: "Storage Used",
            value: "78%",
            change: "-5.1%",
            trend: "down",
            icon: <BarChart3 className="w-6 h-6 text-purple-600" />,
            color: "from-purple-500 to-purple-600"
        },
    ];

    const recentActivities = [
        { user: "Alex Johnson", action: "Created new role", time: "2 min ago", type: "role" },
        { user: "Maria Garcia", action: "Updated permissions", time: "15 min ago", type: "permission" },
        { user: "David Kim", action: "Added new user", time: "1 hour ago", type: "user" },
        { user: "Sarah Wilson", action: "Generated report", time: "2 hours ago", type: "report" },
    ];

    return (
        <div className="min-h-screen bg-background flex">
            {/* Sidebar */}
            <motion.aside
                initial={{ x: sidebarOpen ? 0 : -256 }}
                animate={{ x: sidebarOpen ? 0 : -256 }}
                className="fixed lg:relative h-full bg-white border-r border-gray-200 z-40 transition-all duration-300"
            >
                <div className="h-full flex flex-col w-64">
                    {/* Logo */}
                    <div className="p-6 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-gradient-to-br from-primary-600 to-secondary-600 rounded-xl flex items-center justify-center">
                                    <Shield className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-xl font-bold text-gray-900">TeamHub</h1>
                                    <p className="text-xs text-gray-500">Admin Console</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setSidebarOpen(false)}
                                className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 p-4">
                        <div className="space-y-1">
                            {menuItems.map((item, index) => (
                                <motion.div
                                    key={index}
                                    whileHover={{ x: 4 }}
                                    transition={{ type: "spring", stiffness: 400, damping: 25 }}
                                >
                                    <Link
                                        to={item.path}
                                        className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-700 hover:text-primary-600 hover:bg-primary-50 transition-colors group"
                                    >
                                        <div className="text-gray-400 group-hover:text-primary-600">
                                            {item.icon}
                                        </div>
                                        <span className="font-medium">{item.label}</span>
                                    </Link>
                                </motion.div>
                            ))}
                        </div>
                    </nav>

                    {/* User Profile */}
                    <div className="p-4 border-t border-gray-200">
                        <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                            <div className="relative">
                                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-semibold">
                    {user?.email?.charAt(0).toUpperCase() || 'A'}
                  </span>
                                </div>
                                <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                    {user?.email || 'Administrator'}
                                </p>
                                <p className="text-xs text-gray-500">Super Admin</p>
                            </div>
                            <button
                                onClick={handleLogout}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                title="Logout"
                            >
                                <LogOut className="w-4 h-4 text-gray-400 hover:text-red-500" />
                            </button>
                        </div>
                    </div>
                </div>
            </motion.aside>

            {/* Overlay for mobile */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 lg:hidden z-30"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Main Content */}
            <main className="flex-1 overflow-auto">
                {/* Header */}
                <header className="bg-white border-b border-gray-200 sticky top-0 z-20">
                    <div className="px-6 py-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <button
                                    onClick={() => setSidebarOpen(true)}
                                    className="p-2 hover:bg-gray-100 rounded-lg lg:hidden"
                                >
                                    <Menu className="w-5 h-5" />
                                </button>
                                <div className="relative flex-1 max-w-md">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search users, roles, permissions..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 focus:outline-none"
                                    />
                                </div>
                            </div>

                            <div className="flex items-center space-x-4">
                                <button className="relative p-2 hover:bg-gray-100 rounded-lg">
                                    <Bell className="w-5 h-5 text-gray-600" />
                                    <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
                                </button>
                                <button className="p-2 hover:bg-gray-100 rounded-lg">
                                    <Calendar className="w-5 h-5 text-gray-600" />
                                </button>
                                <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg" />
                            </div>
                        </div>
                    </div>
                </header>

                {/* Dashboard Content */}
                <div className="p-6">
                    {/* Welcome Banner */}
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-8"
                    >
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">
                            Welcome back, Administrator
                        </h1>
                        <p className="text-gray-600">
                            Here's what's happening with your system today.
                        </p>
                    </motion.div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        {stats.map((stat, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="card hover:shadow-medium"
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color} bg-opacity-10`}>
                                        {stat.icon}
                                    </div>
                                    <button className="p-1 hover:bg-gray-100 rounded">
                                        <MoreVertical className="w-5 h-5 text-gray-400" />
                                    </button>
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</h3>
                                <p className="text-sm text-gray-600 mb-2">{stat.label}</p>
                                <div className="flex items-center">
                                    {stat.trend === "up" ? (
                                        <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                                    ) : (
                                        <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
                                    )}
                                    <span className={`text-sm font-medium ${stat.trend === "up" ? "text-green-600" : "text-red-600"}`}>
                    {stat.change}
                  </span>
                                    <span className="text-gray-500 text-sm ml-2">from last month</span>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    <div className="grid lg:grid-cols-3 gap-6">
                        {/* Recent Activity */}
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="lg:col-span-2"
                        >
                            <div className="card">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-bold text-gray-900">Recent Activity</h2>
                                    <button className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                                        View all
                                    </button>
                                </div>
                                <div className="space-y-4">
                                    {recentActivities.map((activity, index) => (
                                        <motion.div
                                            key={index}
                                            initial={{ opacity: 0, x: -10 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: index * 0.05 }}
                                            className="flex items-center p-3 rounded-lg hover:bg-gray-50 transition-colors"
                                        >
                                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 ${
                                                activity.type === "role" ? "bg-blue-100" :
                                                    activity.type === "permission" ? "bg-green-100" :
                                                        activity.type === "user" ? "bg-purple-100" :
                                                            "bg-yellow-100"
                                            }`}>
                        <span className={`text-sm font-medium ${
                            activity.type === "role" ? "text-blue-600" :
                                activity.type === "permission" ? "text-green-600" :
                                    activity.type === "user" ? "text-purple-600" :
                                        "text-yellow-600"
                        }`}>
                          {activity.user.split(' ').map(n => n[0]).join('')}
                        </span>
                                            </div>
                                            <div className="flex-1">
                                                <p className="font-medium text-gray-900">{activity.user}</p>
                                                <p className="text-sm text-gray-600">{activity.action}</p>
                                            </div>
                                            <span className="text-xs text-gray-500">{activity.time}</span>
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        </motion.div>

                        {/* System Overview */}
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                        >
                            <div className="card h-full">
                                <h2 className="text-xl font-bold text-gray-900 mb-6">System Overview</h2>
                                <div className="text-center mb-6">
                                    <img
                                        src={dashboardImg}
                                        alt="Dashboard"
                                        className="w-full max-w-xs mx-auto mb-4"
                                    />
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                        Admin Control Center
                                    </h3>
                                    <p className="text-gray-600 text-sm mb-4">
                                        Monitor and manage your organization's access control and security settings.
                                    </p>
                                </div>

                                <div className="space-y-3">
                                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                        <span className="text-sm font-medium text-gray-700">Security Level</span>
                                        <span className="badge-success">High</span>
                                    </div>
                                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                        <span className="text-sm font-medium text-gray-700">Last Backup</span>
                                        <span className="text-gray-600 text-sm">2 hours ago</span>
                                    </div>
                                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                        <span className="text-sm font-medium text-gray-700">Alerts</span>
                                        <span className="badge-warning">3 Active</span>
                                    </div>
                                </div>

                                <button className="w-full mt-6 btn-primary">
                                    Generate System Report
                                </button>
                            </div>
                        </motion.div>
                    </div>

                    {/* Quick Actions */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="mt-8"
                    >
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Quick Actions</h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {menuItems.map((item, index) => (
                                <motion.div
                                    key={index}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <Link
                                        to={item.path}
                                        className="card group hover:border-primary-300 text-center p-6"
                                    >
                                        <div className="w-12 h-12 bg-gradient-to-br from-primary-100 to-primary-50 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                                            <div className="text-primary-600">
                                                {item.icon}
                                            </div>
                                        </div>
                                        <h3 className="font-semibold text-gray-900 mb-1">{item.label}</h3>
                                        <p className="text-sm text-gray-600">Click to manage</p>
                                    </Link>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </main>
        </div>
    );
};

export default AdminDashboard;